import { FC, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Skeleton } from 'src/components/Skeleton'
import { useGetInboxAvailablePlatformsQuery } from 'src/reactQuery/useGetInboxAvailablePlatformsQuery'
import { useGetTopicBreakdownQuery } from 'src/reactQuery/useGetTopicBreakdownQuery'
import classes from './conversationCategorization.module.scss'

type BreakdownMode = 'topic' | 'subtopic'
type SegmentKey = 'positive' | 'neutral' | 'negative'

const PLATFORM_OPTIONS = [
  { id: 'all', label: 'All Platforms' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'booking', label: 'Booking' },
  { id: 'expedia', label: 'Expedia' },
] as const

interface ConversationCategorizationProps {
  fromDate?: string
  isLoading?: boolean
  pageId?: string
  toDate?: string
}

interface BreakdownEntry {
  id: string
  name: string
  negative: number
  neutral: number
  positive: number
  total: number
}

interface TooltipState {
  entry: BreakdownEntry
  segment: SegmentKey
  x: number
  y: number
}

const TICK_COUNT = 8

// From conversationCategorization.module.scss: .chartBody stacks .row items
// with an 18px gap, each row is a `140px 1fr` grid whose plot cell
// (.chartPlotArea) is exactly 26px tall, and the bar caps are radius 200.
// Topic mode renders up to 8 rows (see the .slice(0, 8) below), so the
// skeleton reserves that same footprint.
const SKELETON_ROW_HEIGHT = 26
const SKELETON_BAR_RADIUS = 200
// Staggered widths so the placeholder reads as a ranked bar chart rather than
// a block of identical bars.
const SKELETON_BAR_WIDTHS = [
  '92%',
  '78%',
  '66%',
  '57%',
  '48%',
  '39%',
  '30%',
  '22%',
]

const SEGMENT_META: Record<SegmentKey, { color: string; label: string }> = {
  negative: { color: '#FF6A33', label: 'Negative' },
  neutral: { color: '#C7CED1', label: 'Neutral' },
  positive: { color: '#8080FF', label: 'Positive' },
}

const getNiceStep = (maxValue: number, steps: number) => {
  if (maxValue <= steps) return 1

  const roughStep = maxValue / steps
  const magnitude = 10 ** Math.floor(Math.log10(roughStep))
  const normalized = roughStep / magnitude

  if (normalized <= 1) return magnitude
  if (normalized <= 2) return 2 * magnitude
  if (normalized <= 5) return 5 * magnitude

  return 10 * magnitude
}

const formatAxisValue = (value: number) => {
  if (value >= 1000) return `${value}`
  return String(value)
}

const formatCount = (value: number) => value.toLocaleString('en-US')

const TruncatedLabel: FC<{ text: string }> = ({ text }) => {
  const ref = useRef<HTMLSpanElement>(null)
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<{
    left: number
    top: number
  } | null>(null)

  const handleEnter = () => {
    const element = ref.current
    if (!element || element.scrollWidth <= element.clientWidth) return
    const rect = element.getBoundingClientRect()
    setPosition({ left: rect.left + rect.width / 2, top: rect.top - 10 })
    setOpen(true)
  }

  return (
    <>
      <span
        className={classes.topicLabelText}
        onMouseEnter={handleEnter}
        onMouseLeave={() => setOpen(false)}
        ref={ref}
      >
        {text}
      </span>
      {open &&
        position &&
        createPortal(
          <div
            className={classes.labelTooltip}
            style={{ left: position.left, top: position.top }}
          >
            {text}
          </div>,
          document.body
        )}
    </>
  )
}

export const ConversationCategorization: FC<
  ConversationCategorizationProps
> = ({ fromDate, isLoading: externalLoading, pageId = '', toDate }) => {
  const [mode, setMode] = useState<BreakdownMode>('topic')
  const { data: availablePlatforms } = useGetInboxAvailablePlatformsQuery({
    fromDate,
    pageId,
    toDate,
  })
  const platformOptions = PLATFORM_OPTIONS.filter(
    (platform) =>
      platform.id === 'all' || (availablePlatforms ?? []).includes(platform.id)
  )

  const [selectedPlatform, setSelectedPlatform] = useState<string>('all')
  const [tooltip, setTooltip] = useState<TooltipState | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const { data, isLoading: queryLoading } = useGetTopicBreakdownQuery({
    fromDate,
    pageId,
    platforms: selectedPlatform === 'all' ? undefined : [selectedPlatform],
    toDate,
  })

  const breakdownEntries = useMemo(() => {
    return (data?.topics || [])
      .map((t) => ({
        id: t.id,
        name: t.name,
        negative: t.negative,
        neutral: t.neutral,
        positive: t.positive,
        total: t.total,
      }))
      .filter((e) => e.total > 0)
      .slice(0, 8)
  }, [data])

  const groupedSubtopics = useMemo(() => {
    if (mode !== 'subtopic') return []
    return (data?.topics || [])
      .filter((t) => t.children && t.children.length > 0 && t.total > 0)
      .map((t) => ({
        children: t.children
          .filter((c) => c.total > 0)
          .slice(0, 8)
          .map((c) => ({
            id: c.id,
            name: c.name,
            negative: c.negative,
            neutral: c.neutral,
            positive: c.positive,
            total: c.total,
          })),
        parentId: t.id,
        parentName: t.name,
      }))
      .slice(0, 8)
  }, [data, mode])

  // For the axis scale — use all child entries when in subtopic mode
  const allSubtopicEntries = useMemo(
    () => groupedSubtopics.flatMap((g) => g.children),
    [groupedSubtopics]
  )

  const activeEntries =
    mode === 'subtopic' ? allSubtopicEntries : breakdownEntries

  const maxTotal = activeEntries.reduce(
    (largest, entry) => Math.max(largest, entry.total),
    0
  )
  const tickStep = getNiceStep(Math.max(maxTotal, 1), TICK_COUNT)
  const xMax = Math.max(tickStep * TICK_COUNT, tickStep)
  const ticks = Array.from(
    { length: TICK_COUNT + 1 },
    (_, index) => index * tickStep
  )
  const isLoading = externalLoading || queryLoading
  // First load only. On a refetch (platform tab, date range) `data` is still
  // there, so the real bars stay on screen instead of flashing back to
  // placeholders.
  const isInitialLoading = isLoading && !data

  return (
    <div className={classes.container} ref={containerRef}>
      <div className={classes.header}>
        <h2 className={classes.title}>Conversations Topic Breakdown</h2>
        <div className={classes.toggle}>
          <button
            className={`${classes.toggleButton} ${
              mode === 'topic' ? classes.active : ''
            }`}
            onClick={() => setMode('topic')}
            type="button"
          >
            Topic
          </button>
          <button
            className={`${classes.toggleButton} ${
              mode === 'subtopic' ? classes.active : ''
            }`}
            onClick={() => setMode('subtopic')}
            type="button"
          >
            Subtopic
          </button>
        </div>
      </div>

      <div className={classes.platformTabs}>
        {platformOptions.map((platform) => (
          <button
            className={`${classes.platformTab} ${
              selectedPlatform === platform.id ? classes.activePlatform : ''
            }`}
            key={platform.id}
            onClick={() => setSelectedPlatform(platform.id)}
            type="button"
          >
            {platform.label}
          </button>
        ))}
      </div>

      {tooltip && (
        <div
          className={classes.tooltipBox}
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          <div className={classes.tooltipTitle}>
            {tooltip.entry.name} Messages
          </div>
          <div className={classes.tooltipTotal}>
            {formatCount(tooltip.entry.total)}
          </div>
          {(['positive', 'neutral', 'negative'] as SegmentKey[]).map(
            (segment) => (
              <div className={classes.tooltipRow} key={segment}>
                <span
                  className={classes.tooltipDot}
                  style={{ background: SEGMENT_META[segment].color }}
                />
                <span className={classes.tooltipLabel}>
                  {SEGMENT_META[segment].label}
                </span>
                <strong className={classes.tooltipValue}>
                  {formatCount(tooltip.entry[segment])}
                </strong>
              </div>
            )
          )}
        </div>
      )}

      {isInitialLoading ? (
        <div className={classes.chartCard}>
          <div className={classes.plotSection}>
            <div className={classes.chartHeader}>
              <div className={classes.axisSpacer} />
              <div className={classes.chartPlotArea}>
                <Skeleton height={12} width="100%" />
              </div>
            </div>

            <div className={classes.chartBody}>
              {SKELETON_BAR_WIDTHS.map((barWidth, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <div className={classes.row} key={index}>
                  <div className={classes.topicLabel}>
                    <Skeleton height={12} width="80%" />
                  </div>
                  <div className={classes.chartPlotArea}>
                    <Skeleton
                      height={SKELETON_ROW_HEIGHT}
                      radius={SKELETON_BAR_RADIUS}
                      width={barWidth}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* The legend is static chrome — keeping it up while loading holds
              the card's real height. */}
          <div className={classes.legend}>
            {(['positive', 'neutral', 'negative'] as SegmentKey[]).map(
              (segment) => (
                <div className={classes.legendItem} key={segment}>
                  <span
                    className={classes.legendDot}
                    style={{ background: SEGMENT_META[segment].color }}
                  />
                  <span>{SEGMENT_META[segment].label}</span>
                </div>
              )
            )}
          </div>
        </div>
      ) : (
        <div className={classes.chartCard}>
          <div className={classes.plotSection}>
            <div className={classes.gridOverlay}>
              {ticks.slice(1).map((tick, tickIndex) => (
                <span
                  className={classes.gridLine}
                  key={`gl-${tick}`}
                  style={{
                    left: `${((tickIndex + 1) / TICK_COUNT) * 100}%`,
                  }}
                />
              ))}
            </div>
            <div className={classes.chartHeader}>
              <div className={classes.axisSpacer} />
              <div className={classes.chartPlotArea}>
                <div className={classes.axisTicks}>
                  {ticks.slice(0, -1).map((tick, i) => (
                    <span
                      className={classes.axisTick}
                      key={tick}
                      style={{
                        left: i === 0 ? '0%' : `${(i / TICK_COUNT) * 100}%`,
                      }}
                    >
                      {formatAxisValue(tick)}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className={classes.chartBody}>
              {/* ── Topic mode: flat list ── */}
              {mode === 'topic' &&
                breakdownEntries.map((entry) => {
                  const segments = (
                    ['positive', 'neutral', 'negative'] as SegmentKey[]
                  )
                    .map((key) => ({
                      color: SEGMENT_META[key].color,
                      key,
                      value: entry[key],
                      width: (entry[key] / xMax) * 100,
                    }))
                    .filter((segment) => segment.value > 0)

                  return (
                    <div className={classes.row} key={entry.id}>
                      <div className={classes.topicLabel}>
                        <TruncatedLabel text={entry.name} />
                      </div>
                      <div className={classes.chartPlotArea}>
                        <div className={classes.track}>
                          {segments.map((segment, index) => {
                            const isLast = index === segments.length - 1
                            return (
                              <button
                                className={classes.segment}
                                key={`${entry.id}-${segment.key}`}
                                onMouseEnter={(event) => {
                                  const rect =
                                    containerRef.current?.getBoundingClientRect()
                                  if (!rect) return
                                  setTooltip({
                                    entry,
                                    segment: segment.key,
                                    x: event.clientX - rect.left,
                                    y: event.clientY - rect.top,
                                  })
                                }}
                                onMouseLeave={() => setTooltip(null)}
                                onMouseMove={(event) => {
                                  const rect =
                                    containerRef.current?.getBoundingClientRect()
                                  if (!rect) return
                                  setTooltip({
                                    entry,
                                    segment: segment.key,
                                    x: event.clientX - rect.left,
                                    y: event.clientY - rect.top,
                                  })
                                }}
                                style={{
                                  background: segment.color,
                                  borderBottomLeftRadius: 0,
                                  borderBottomRightRadius: isLast ? 200 : 0,
                                  borderTopLeftRadius: 0,
                                  borderTopRightRadius: isLast ? 200 : 0,
                                  width: `${segment.width}%`,
                                }}
                                type="button"
                              >
                                {segment.width > 2.25 && (
                                  <span className={classes.segmentValue}>
                                    {formatCount(segment.value)}
                                  </span>
                                )}
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    </div>
                  )
                })}

              {/* ── Subtopic mode: grouped list ── */}
              {mode === 'subtopic' &&
                groupedSubtopics.map((group) => (
                  <div className={classes.subtopicGroup} key={group.parentId}>
                    {/* Parent label row */}
                    <div className={classes.subtopicParentRow}>
                      <div className={classes.topicLabel}>
                        <span className={classes.subtopicParentLabel}>
                          {group.parentName.charAt(0).toUpperCase() +
                            group.parentName.slice(1)}
                        </span>
                      </div>
                      <div />
                    </div>

                    {/* Children indented with left border */}
                    <div className={classes.subtopicChildren}>
                      {group.children.map((entry) => {
                        const segments = (
                          ['positive', 'neutral', 'negative'] as SegmentKey[]
                        )
                          .map((key) => ({
                            color: SEGMENT_META[key].color,
                            key,
                            value: entry[key],
                            width: (entry[key] / xMax) * 100,
                          }))
                          .filter((s) => s.value > 0)

                        return (
                          <div
                            className={classes.subtopicChildRow}
                            key={entry.id}
                          >
                            <div className={classes.topicLabel}>
                              <TruncatedLabel text={entry.name} />
                            </div>
                            <div className={classes.chartPlotArea}>
                              <div className={classes.track}>
                                {segments.map((segment, index) => {
                                  const isLast = index === segments.length - 1
                                  return (
                                    <button
                                      className={classes.segment}
                                      key={`${entry.id}-${segment.key}`}
                                      onMouseEnter={(event) => {
                                        const rect =
                                          containerRef.current?.getBoundingClientRect()
                                        if (!rect) return
                                        setTooltip({
                                          entry,
                                          segment: segment.key,
                                          x: event.clientX - rect.left,
                                          y: event.clientY - rect.top,
                                        })
                                      }}
                                      onMouseLeave={() => setTooltip(null)}
                                      onMouseMove={(event) => {
                                        const rect =
                                          containerRef.current?.getBoundingClientRect()
                                        if (!rect) return
                                        setTooltip({
                                          entry,
                                          segment: segment.key,
                                          x: event.clientX - rect.left,
                                          y: event.clientY - rect.top,
                                        })
                                      }}
                                      style={{
                                        background: segment.color,
                                        borderBottomLeftRadius: 0,
                                        borderBottomRightRadius: isLast
                                          ? 200
                                          : 0,
                                        borderTopLeftRadius: 0,
                                        borderTopRightRadius: isLast ? 200 : 0,
                                        width: `${segment.width}%`,
                                      }}
                                      type="button"
                                    >
                                      {segment.width > 2.25 && (
                                        <span className={classes.segmentValue}>
                                          {formatCount(segment.value)}
                                        </span>
                                      )}
                                    </button>
                                  )
                                })}
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ))}
            </div>
          </div>

          <div className={classes.legend}>
            {(['positive', 'neutral', 'negative'] as SegmentKey[]).map(
              (segment) => (
                <div className={classes.legendItem} key={segment}>
                  <span
                    className={classes.legendDot}
                    style={{ background: SEGMENT_META[segment].color }}
                  />
                  <span>{SEGMENT_META[segment].label}</span>
                </div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  )
}
